import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils/cn'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

interface PageHeaderProps {
  title: string
  eyebrow?: string
  description?: string
  backTo?: string
  backLabel?: string
  backVariant?: 'outline' | 'ghost'
  backSize?: 'sm' | 'icon'
  onBackClick?: () => void
  actions?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  eyebrow,
  description,
  backTo,
  backLabel = 'Voltar',
  backVariant = 'outline',
  backSize = 'sm',
  onBackClick,
  actions,
  className,
}: PageHeaderProps) {
  const backButton = backTo ? (
    <Button variant={backVariant} size={backSize} asChild>
      <Link to={backTo} aria-label={backSize === 'icon' ? backLabel : undefined}>
        <ArrowLeft className={cn('size-4', backSize === 'icon' && 'h-5 w-5')} />
        {backSize !== 'icon' ? backLabel : null}
      </Link>
    </Button>
  ) : onBackClick ? (
    <Button
      variant={backVariant}
      size={backSize}
      onClick={onBackClick}
      aria-label={backSize === 'icon' ? backLabel : undefined}
    >
      <ArrowLeft className={cn('size-4', backSize === 'icon' && 'h-5 w-5')} />
      {backSize !== 'icon' ? backLabel : null}
    </Button>
  ) : null

  return (
    <header
      data-slot="page-header"
      className={cn(
        'flex min-w-0 flex-col gap-5 rounded-3xl border border-border/70 bg-card/90 p-5 shadow-sm sm:p-6 lg:flex-row lg:items-start lg:justify-between',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {backButton && <div className="mb-4">{backButton}</div>}
        <div className="min-w-0">
          {eyebrow && (
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              {eyebrow}
            </p>
          )}
          <h1 className="break-words text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {actions ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2 lg:max-w-[50%]">{actions}</div>
      ) : null}
    </header>
  )
}

PageHeader.displayName = 'PageHeader'
